import React, { useState, MouseEvent } from 'react';
import { Dispatch } from 'redux';

import styled from 'styled-components';
import { GIButton, Modal } from 'app-components';

import { useAppDispatch } from '../../hooks';
import Input from '../../components/common/form/new/Input';
import { ValidationErrorMessage } from '../../components/common/form';
import useManageWorkgroup from '../../modules/manageworkgroup/hooks/useManageWorkgroup';
import { saveNewWorkgroup } from '../../actions/ticketMgmtPortal/manageWorkgroupActionCreator';

interface IModalProps {
    onClose: (e: MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    editWorkgroupName?: string;
    isDefault?: string;
    id?: number;
}

const StyledModal = styled.div`
    .gi-modal__content {
        width: 600px;
    }
    .gi-modal__body {
        padding: 0;
    }
    .gi-modal__controls {
        display: flex;
        justify-content: center;
        gap: 10px;
    }

    .item-center {
        text-align: center;
        margin-top: 10px;
    }

    .bg-color {
        background-color: ${({ theme }) => theme.dangerLightest};
    }

    .align-input {
        text-align: center;
        margin-top: 4rem;
    }
    .align-label {
        padding-right: 1rem;
    }

    .input-width {
        width: 220px;
    }
`;

export const WorkGroupModal = ({ onClose }: IModalProps) => {
    const dispatch: Dispatch<any> = useAppDispatch();
    const { checkIfWorkgroupNameExists, validateWorkgroupName } = useManageWorkgroup();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [newWorkgroupName, setNewWorkgroupName] = useState<string>('');

    const handleFieldChange = async (workgroupName: string) => {
        setNewWorkgroupName(workgroupName);
        const validWorkgroupName = validateWorkgroupName(workgroupName);
        if (validWorkgroupName?.isError) {
            setErrorMessage(validWorkgroupName?.errorMsg);
        } else {
            const workgroupAlreadyExist = await checkIfWorkgroupNameExists(workgroupName);
            if (workgroupAlreadyExist.isError) {
                setErrorMessage(workgroupAlreadyExist.errorMsg);
            } else {
                setErrorMessage(null);
            }
        }
    };

    const handleSaveNewWorkgroup = e => {
        if (newWorkgroupName.length < 1) {
            setErrorMessage('ticket_management.manage_workgroup.modal.workgroup.form.validation2.msg');
            return;
        }
        dispatch(saveNewWorkgroup(newWorkgroupName));
        onClose(e);
    };

    return (
        <StyledModal>
            <Modal id="manage_workgroup_modal">
                <Modal.Header
                    id="manage_workgroup_header"
                    titleId="ticket_management.manage_workgroup.add_workgroup"
                    onHide={onClose}
                />
                <Modal.Body>
                    <Input
                        label={{
                            labelText: 'ticket_management.form.workgroup.search.input.workgroupName',
                            labelClasses: ['align-label'],
                            wrapperClasses: ['align-input'],
                            isRequired: false,
                        }}
                        input={{
                            id: 'add_new_workgroupName',
                            isInputRequired: false,
                            type: 'text',
                            value: newWorkgroupName,
                            inputClasses: ['input-width'],
                        }}
                        actions={{
                            handleFieldChange: value => handleFieldChange(value),
                        }}
                    />
                    <ValidationErrorMessage
                        errorWrapperClasses={['item-center']}
                        errorClasses={['bg-color']}
                        isVisible={errorMessage !== null}
                        messageText={errorMessage}
                    />
                </Modal.Body>
                <Modal.Controls>
                    <GIButton
                        id="workgroupModalSaveButton"
                        dataTestId="workgroup_modal_save_btn"
                        onClickHandler={handleSaveNewWorkgroup}
                        variant="primary"
                        labelId="button.save"
                        isDisabled={errorMessage !== null || newWorkgroupName.trim().length < 1}
                    />
                    <GIButton
                        id="workgroupModalCancelButton"
                        dataTestId="workgroup_modal_close_btn"
                        onClickHandler={onClose}
                        variant="secondary"
                        labelId="button.cancel"
                    />
                </Modal.Controls>
            </Modal>
        </StyledModal>
    );
};
